import { where } from "firebase/firestore/lite";
import { getLiteDoc, getLiteQueryDocs } from "../../helpers/firestoreLiteQuery.ts";

export interface CasinoWeeklyStat {
    ownerUserName: string;
    activePlayers: number;
    win: number;
    loss: number;
    total: number;
    liveUsed: boolean;
    liveNet: number;
}

const num = (value: unknown): number => {
    const amount = Number(value ?? 0);
    return Number.isFinite(amount) ? amount : 0;
};

const docId = (userName: string, weekId: string): string =>
    `${userName.replace(/\//g, "_")}_${weekId}`;

const emptyStat = (userName: string): CasinoWeeklyStat => ({
    ownerUserName: userName,
    activePlayers: 0,
    win: 0,
    loss: 0,
    total: 0,
    liveUsed: false,
    liveNet: 0,
});

const mapStat = (userName: string, data: Record<string, unknown>): CasinoWeeklyStat => ({
    ownerUserName: String(data.ownerUserName ?? userName),
    activePlayers: num(data.activePlayers),
    win: num(data.win),
    loss: num(data.loss),
    total: num(data.total),
    liveUsed: data.liveUsed === true,
    liveNet: num(data.liveNet),
});

/**
 * Reads the week written from casino play.
 * The owner row is that account's sum. The rows under it are only the
 * agents directly below, each one already the sum of their own players.
 */
export async function readCasinoWeeklyStats(
    userName: string,
    weekId: string,
): Promise<{ owner: CasinoWeeklyStat; agents: CasinoWeeklyStat[] }> {
    const name = userName.trim();
    if (!name) {
        return { owner: emptyStat(""), agents: [] };
    }

    const [ownerSnap, childUsers] = await Promise.all([
        getLiteDoc("casinoWeeklyStats", docId(name, weekId)),
        getLiteQueryDocs("users", [where("parent", "==", name)]),
    ]);
    const owner = ownerSnap.exists()
        ? mapStat(name, ownerSnap.data() ?? {})
        : emptyStat(name);

    const childNames = childUsers
        .map((docSnap) => docSnap.data())
        .filter((user) => user.userType === "subAgent")
        .map((user) => String(user.userName ?? "").trim())
        .filter((childName) => childName.length > 0)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));

    const agents = await Promise.all(childNames.map(async (childName) => {
        const snap = await getLiteDoc("casinoWeeklyStats", docId(childName, weekId));
        if (!snap.exists()) {
            return emptyStat(childName);
        }
        return mapStat(childName, snap.data() ?? {});
    }));
    return { owner, agents };
}
